import type { CardDef, CardType } from "@/intake-v2/types";
import { OPTION_CODE_TO_LABEL } from "./intakeOptionMaps";
import { RESPONSE_QUESTION_KEYS } from "./patientIntakeApi";
import type { DocumentReferenceLink, IntakeData } from "./types";

/**
 * Inverse of `buildFormPayload` (patientIntakeApi.ts): server form -> client state.
 *
 * Used by the OTP-gated continuation route, where a patient answered part of the
 * questionnaire by voice and finishes it in the browser.
 *
 * Deliberately NOT covered:
 *  - uploads / photos / media of any kind (a spoken answer never satisfies a photo card,
 *    and media is fetched separately through `getFormMedia`)
 *  - `consentGiven` / `emailVerified` (consent is re-taken in the browser)
 *  - Spanish option labels: `buildFormPayload` only ever writes the English labels
 *    from OPTION_CODE_TO_LABEL, so only those are decoded back to codes.
 */

const RESPONSE_KEY_SET = new Set<string>(RESPONSE_QUESTION_KEYS);

const DOCUMENT_LINK_ID = /^documentReferenceLinks_\d+$/;

/** label -> code, lazily inverted per question id. */
const labelToCodeCache = new Map<string, Record<string, string>>();

const labelToCode = (questionId: string): Record<string, string> | undefined => {
  const cached = labelToCodeCache.get(questionId);
  if (cached) return cached;
  const codeToLabel = OPTION_CODE_TO_LABEL[questionId];
  if (!codeToLabel) return undefined;
  const inverted: Record<string, string> = {};
  for (const [code, label] of Object.entries(codeToLabel)) {
    // First writer wins: option maps are code-unique, labels are near-unique.
    if (!(label in inverted)) inverted[label] = code;
  }
  labelToCodeCache.set(questionId, inverted);
  return inverted;
};

/** Answers come back as labels; store the original code when we recognise one. */
const decodeAnswerValue = (questionId: string, value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const codeToLabel = OPTION_CODE_TO_LABEL[questionId];
  if (!codeToLabel) return value;
  if (value in codeToLabel) return value; // already a code
  return labelToCode(questionId)?.[value] ?? value;
};

const decodeAnswer = (questionId: string, answer: unknown): unknown => {
  if (Array.isArray(answer)) {
    return answer.map((item) => decodeAnswerValue(questionId, item));
  }
  return decodeAnswerValue(questionId, answer);
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Enum -> canonical procedure slug. `getSpecialtyTag` is many-to-one, so this is a
 * best-effort fallback only: an explicit `procedureSlug` on the form always wins.
 */
const SPECIALTY_ENUM_TO_SLUG: Record<string, string> = {
  dentistry: "dentistry",
  vascular: "vascular",
  hair_transplant: "hair-transplant",
  optical: "optical",
  plastic_surgery: "plastic-surgery",
  diagnostics_imaging: "diagnostics",
  orthopedic: "orthopedics",
  fertility: "fertility",
  stem_cells: "stem-cell",
  bariatric: "bariatric",
  wellness_healthcare: "wellness",
};

const IDENTITY_STRING_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "country",
  "dateOfBirth",
  "city",
  "state",
] as const;

type ServerForm = Record<string, unknown> | null | undefined;

export function hydrateIntakeData(form: ServerForm): Partial<IntakeData> {
  const root = asRecord(form);
  if (!root) return {};
  // Tolerate both the bare form and a `{ data: form }` envelope.
  const source = asRecord(root.data) ?? root;

  const out: Partial<IntakeData> = {};

  const formUid = asTrimmedString(source.formUid ?? source.form_uid);
  if (formUid) out.formUid = formUid;
  const referenceUid = asTrimmedString(
    source.referenceUid ?? source.reference_uid,
  );
  if (referenceUid) out.referenceUid = referenceUid;

  // ── identity ────────────────────────────────────────────────────────────
  const identity = asRecord(source.identity_json) ?? asRecord(source.identity) ?? {};
  for (const field of IDENTITY_STRING_FIELDS) {
    const value = asTrimmedString(identity[field] ?? source[field]);
    if (value) (out as Record<string, unknown>)[field] = value;
  }
  const language = asTrimmedString(identity.preferredLanguage);
  if (language === "en" || language === "es") {
    out.preferredLanguage = language;
  }

  // ── procedure ───────────────────────────────────────────────────────────
  const procedureSlug =
    asTrimmedString(source.procedureSlug) ??
    asTrimmedString(asRecord(source.procedure)?.slug) ??
    asTrimmedString(source.procedure) ??
    SPECIALTY_ENUM_TO_SLUG[asTrimmedString(source.specialty) ?? ""];
  if (procedureSlug) out.procedures = procedureSlug;

  // ── responses ───────────────────────────────────────────────────────────
  const responses = source.responses_json ?? source.responses;
  if (!Array.isArray(responses)) return out;

  const documentLinks: DocumentReferenceLink[] = [];

  for (const entry of responses) {
    const record = asRecord(entry);
    if (!record) continue;
    const questionId = asTrimmedString(record.questionId ?? record.question_id);
    if (!questionId) continue;
    const answer = record.answer;
    if (answer === null || answer === undefined) continue;

    if (DOCUMENT_LINK_ID.test(questionId)) {
      const url = asTrimmedString(answer);
      if (url) documentLinks.push({ url });
      continue;
    }

    // Unknown question ids (renamed fields, Jenny-only questions) are dropped.
    if (!RESPONSE_KEY_SET.has(questionId)) continue;

    (out as Record<string, unknown>)[questionId] = decodeAnswer(
      questionId,
      answer,
    );
  }

  if (documentLinks.length > 0) out.documentReferenceLinks = documentLinks;

  return out;
}

// ─── Resume position ──────────────────────────────────────────────────────────

/**
 * Card types a spoken answer can satisfy. Everything else — photo, upload,
 * document, visual picker, review, terminal — is browser-only and must still be
 * shown even when the questionnaire around it is fully answered.
 */
const RESUMABLE_CARD_TYPES: ReadonlySet<CardType> = new Set<CardType>([
  "text-input",
  "email-input",
  "name-input",
  "contact-info",
  "phone-country",
  "language-select",
  "procedure-select",
  "single-choice",
  "multi-choice",
]);

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const isAnswered = (card: CardDef, data: Partial<IntakeData>): boolean => {
  const read = (key: string) => (data as Record<string, unknown>)[key];

  switch (card.cardType) {
    case "name-input":
      return hasValue(read("firstName")) && hasValue(read("lastName"));
    case "contact-info":
    case "phone-country":
      return (
        hasValue(read("email")) &&
        hasValue(read("phone")) &&
        hasValue(read("country"))
      );
    case "language-select":
      return hasValue(read("preferredLanguage"));
    case "procedure-select": {
      const procedures = read("procedures");
      return typeof procedures === "string"
        ? procedures.trim().length > 0
        : hasValue((procedures as { slug?: string } | undefined)?.slug);
    }
    default:
      return hasValue(read((card.fieldKey as string) ?? card.id));
  }
};

/**
 * First card the patient still has to deal with: skips answered speakable cards,
 * always stops at a photo/upload/document/review card.
 * Returns 0 when nothing is answered, and the last card when everything is.
 */
export function findFirstIncompleteCardIndex(
  cards: readonly CardDef[],
  data: Partial<IntakeData>,
): number {
  for (let index = 0; index < cards.length; index += 1) {
    const card = cards[index];
    if (!RESUMABLE_CARD_TYPES.has(card.cardType)) return index;
    if (!isAnswered(card, data)) return index;
  }
  return Math.max(cards.length - 1, 0);
}
